import { useCallRouteV1QueryUpdaters } from '../query-updaters';
import { SaveIO } from '../types';
import { UseCallRouteV1MutationEndpointArgs, useCallRouteV1Mutation } from './use-call-route-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `Save` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useSaveMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends SaveIO['input'] = SaveIO['input']
>({
  options,
  // optimisticUpdate = false,
  ...args
}: UseCallRouteV1MutationEndpointArgs<'Save', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const { updateQuery, invalidateQueries } = useCallRouteV1QueryUpdaters();

  return useCallRouteV1Mutation<'Save', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'Save',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here

        // When successfully updating a call route, update the Read query cached call route
        // to reflect the new updated fields
        updateQuery({
          endpointName: 'Read',
          queryFilters: {},
          updater: (data) => ({
            ...data,
            nodes: response.nodes,
            edges: response.edges,
          }),
        });

        invalidateQueries({
          endpointName: 'Usage',
        });

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
